import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Toolbar,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';

const navItems = [
  { text: 'Home', href: '#home' },
  { text: 'Features', href: '#features' },
  { text: 'How It Works', href: '#how-it-works' },
];

const LandingHeader = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleAuth = () => {
    window.scrollTo(0, 0);
    navigate('/auth');
  };

  const scrollTo = (href) => {
    const id = href.replace('#', '');
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setDrawerOpen(false);
  };

  const drawer = (
    <Box sx={{ width: 260, display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#0d1b3e' }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <List sx={{ flex: 1 }}>
        {navItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton onClick={() => scrollTo(item.href)} sx={{ py: 1.5, px: 3 }}>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <Button
          fullWidth
          variant="outlined"
          onClick={handleAuth}
          sx={{ borderColor: 'rgba(255,255,255,0.35)', color: 'white', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.07)' }, textTransform: 'none', fontWeight: 600 }}
        >
          Login
        </Button>
        <Button
          fullWidth
          variant="contained"
          onClick={handleAuth}
          endIcon={<ArrowForwardIcon />}
          sx={{ bgcolor: '#2196F3', '&:hover': { bgcolor: '#1976D2' }, textTransform: 'none', fontWeight: 700 }}
        >
          Get Started Free
        </Button>
      </Box>
    </Box>
  );

  return (
    <AppBar
      position="fixed"
      color="transparent"
      elevation={0}
      sx={{
        transition: 'background-color 0.3s ease, border-color 0.3s ease, backdrop-filter 0.3s ease',
        backgroundColor: scrolled ? 'rgba(10, 18, 48, 0.78)' : 'transparent',
        backdropFilter: scrolled ? 'blur(14px)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(14px)' : 'none',
        borderBottom: '1px solid',
        borderColor: scrolled ? 'rgba(255,255,255,0.08)' : 'transparent',
        borderRadius: 0,
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ py: 0.5 }}>
          {/* Logo */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mr: 4, gap: 1 }}
            onClick={() => { window.scrollTo(0, 0); }}
          >
            <Box
              component="img"
              src="/logo.png"
              alt="CircleMoments"
              sx={{ height: 36, filter: 'brightness(0) invert(1)' }}
            />
            <Typography variant="h6" sx={{ fontWeight: 800, color: 'white', letterSpacing: '-0.3px' }}>
              CircleMoments
            </Typography>
          </Box>

          {isMobile ? (
            <Box sx={{ ml: 'auto' }}>
              <IconButton size="large" onClick={() => setDrawerOpen(true)} sx={{ color: 'white' }}>
                <MenuIcon />
              </IconButton>
            </Box>
          ) : (
            <>
              {/* Desktop nav */}
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                {navItems.map((item) => (
                  <Button
                    key={item.text}
                    onClick={() => scrollTo(item.href)}
                    sx={{
                      color: 'rgba(255,255,255,0.78)',
                      fontWeight: 500,
                      fontSize: '0.95rem',
                      px: 1.75,
                      py: 1,
                      textTransform: 'none',
                      borderRadius: 1.5,
                      '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.07)' },
                    }}
                  >
                    {item.text}
                  </Button>
                ))}
              </Box>

              {/* Desktop CTA */}
              <Box sx={{ ml: 'auto', display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <Button
                  variant="text"
                  onClick={handleAuth}
                  sx={{
                    color: 'rgba(255,255,255,0.78)',
                    fontWeight: 500,
                    textTransform: 'none',
                    px: 2,
                    '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.07)' },
                  }}
                >
                  Login
                </Button>
                <Button
                  variant="contained"
                  onClick={handleAuth}
                  sx={{
                    bgcolor: '#2196F3',
                    color: 'white',
                    fontWeight: 700,
                    textTransform: 'none',
                    px: 2.5,
                    py: 0.9,
                    borderRadius: 1.5,
                    fontSize: '0.9rem',
                    boxShadow: '0 0 18px rgba(33,150,243,0.35)',
                    '&:hover': { bgcolor: '#1976D2', boxShadow: '0 0 28px rgba(33,150,243,0.5)' },
                  }}
                >
                  Get Started Free
                </Button>
              </Box>
            </>
          )}
        </Toolbar>
      </Container>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        {drawer}
      </Drawer>
    </AppBar>
  );
};

export default LandingHeader;
